import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Outlet, useLocation, useNavigate, useMatch } from 'react-router-dom';
import Box from '@mui/material/Box';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import ExploreIcon from '@mui/icons-material/Explore';
import PersonIcon from '@mui/icons-material/Person';
import TimerIcon from '@mui/icons-material/Timer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TranslateIcon from '@mui/icons-material/Translate';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import LANGUAGES from '../../i18n/languages';
import AppHeader from '../../components/AppHeader';

import Discover from './Discover';
import Me from './Me';
import QuickScore from './QuickScore';

const TAB_ROUTES = ['/app', '/app/me', '/app/quickscore'];

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);

  // Detect if we're on a main tab vs a sub-page
  const [serverVersion, setServerVersion] = useState('...');
  useEffect(() => {
    const base = import.meta.env.VITE_API_URL || '';
    axios.get(`${base}/api/version`).then(({ data }) => setServerVersion(data.version || '?')).catch(() => setServerVersion('?'));
  }, []);

  const isDiscoverTab = location.pathname === '/app' || location.pathname === '/app/';
  const isMeTab = location.pathname === '/app/me' || location.pathname === '/app/me/';
  const isQuickScoreTab = location.pathname === '/app/quickscore' || location.pathname === '/app/quickscore/';
  const isMainTab = isDiscoverTab || isMeTab || isQuickScoreTab;

  const activeTab = isDiscoverTab ? 0
    : location.pathname.startsWith('/app/me') ? 1
    : location.pathname.startsWith('/app/quickscore') ? 2
    : -1;

  const handleTabChange = (_, newValue) => {
    if (newValue === 3) {
      window.open('https://www.endofscoring.com', '_blank');
      return;
    }
    navigate(TAB_ROUTES[newValue]);
  };

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('userLanguage', code);
    setLangMenuAnchor(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: 600, mx: 'auto' }}>
      <AppHeader />
      <Box sx={{ position: 'fixed', top: 4, right: 8, zIndex: 1200 }}>
        <IconButton color="inherit" size="small" onClick={(e) => setLangMenuAnchor(e.currentTarget)}
          sx={{ bgcolor: 'rgba(255,255,255,0.85)', boxShadow: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' } }}>
          <TranslateIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, pb: '56px', overflow: 'auto', position: 'relative' }}>
        {/* Keep main tabs always mounted, toggle visibility */}
        <Box sx={{ display: isDiscoverTab ? 'block' : 'none' }}>
          <Discover />        </Box>
        <Box sx={{ display: isMeTab ? 'block' : 'none' }}>
          <Me />        </Box>
        <Box sx={{ display: isQuickScoreTab ? 'block' : 'none' }}>
          <QuickScore />        </Box>

        {/* Sub-pages (me/:matchId, quickscore/:matchIndex, etc.) render via Outlet */}
        {!isMainTab && <Outlet />}

        <Menu
          anchorEl={langMenuAnchor}
          open={Boolean(langMenuAnchor)}
          onClose={() => setLangMenuAnchor(null)}
        >
          <MenuItem disabled sx={{ opacity: 0.6, minHeight: 0, py: 0.5, justifyContent: 'center', flexDirection: 'column' }}>
            <Typography variant="caption" color="text.secondary">Client: {__APP_VERSION__}</Typography>
            <Typography variant="caption" color="text.secondary">Server: {serverVersion}</Typography>
          </MenuItem>
          <Divider />
          {LANGUAGES.map((lang) => (
            <MenuItem
              key={lang.code}
              selected={i18n.language === lang.code}
              onClick={() => handleLanguageChange(lang.code)}
            >
              {lang.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <BottomNavigation
        value={activeTab >= 0 ? activeTab : 0}
        onChange={handleTabChange}
        showLabels
        sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10, maxWidth: 600, mx: 'auto' }}
      >
        <BottomNavigationAction label={t('app.discover')} icon={<ExploreIcon />} />
        <BottomNavigationAction label={t('app.me')} icon={<PersonIcon />} />
        <BottomNavigationAction label={t('app.quickscore')} icon={<TimerIcon />} />
        <BottomNavigationAction label="" icon={<img src="/logo.png" alt="Aria" style={{ width: 32, height: 32, objectFit: 'contain' }} />} />
      </BottomNavigation>
    </Box>
  );
}
